/**
 * Virtual Visit manifest helpers (no React, no three.js).
 *
 * A Visit is many rooms. Each room has one splat asset (SOG preferred),
 * a transform, a name and a floor index. Legacy single-file Visits are a
 * one-room Visit. The OS API (GET /api/public/visits/{id}) returns this
 * shape; share/listing/desk build it from a bare file_url when needed.
 */

export const VISIT_FORMATS = ["sog", "spz", "ply", "splat", "ksplat"];

/** Accept attribute for every Visit upload input (desk + GO parity). */
export const VISIT_ACCEPT = ".ply,.splat,.spz,.sog";

/**
 * Default orientation for splats trained in COLMAP/OpenCV space (y-down).
 * Rotates 180° about X so "up" is up in three.js. A room transform with an
 * explicit rotation overrides it.
 */
export const DEFAULT_ROTATION = [1, 0, 0, 0];

export function formatFromUrl(url) {
  const raw = String(url || "").trim().toLowerCase();
  if (!raw) return null;
  const path = raw.split("?")[0].split("#")[0];
  if (path.endsWith("meta.json")) return "sog-meta";
  const m = path.match(/\.([a-z0-9]+)$/);
  if (!m) return null;
  return VISIT_FORMATS.includes(m[1]) ? m[1] : null;
}

/** Spark SplatFileType string for a manifest format. */
export function sparkFileType(format) {
  switch (String(format || "").toLowerCase()) {
    case "sog":
      return "pcsogszip";
    case "sog-meta":
      return "pcsogs";
    case "spz":
      return "spz";
    case "ply":
      return "ply";
    case "splat":
      return "splat";
    case "ksplat":
      return "ksplat";
    default:
      return undefined;
  }
}

function num3(v, fallback) {
  if (!Array.isArray(v) || v.length !== 3) return fallback;
  const out = v.map(Number);
  return out.every(Number.isFinite) ? out : fallback;
}

function num4(v, fallback) {
  if (!Array.isArray(v) || v.length !== 4) return fallback;
  const out = v.map(Number);
  return out.every(Number.isFinite) ? out : fallback;
}

export function normalizeTransform(t) {
  const src = t && typeof t === "object" ? t : {};
  const scaleRaw = Number(src.scale);
  return {
    position: num3(src.position, [0, 0, 0]),
    rotation: num4(src.rotation, DEFAULT_ROTATION.slice()),
    scale: Number.isFinite(scaleRaw) && scaleRaw > 0 ? scaleRaw : 1,
  };
}

function normalizeBox(b) {
  if (!b || typeof b !== "object") return null;
  const min = num3(b.min, null);
  const max = num3(b.max, null);
  if (!min || !max) return null;
  return { min, max };
}

function normalizePose(p) {
  if (!p || typeof p !== "object") return null;
  const eye = num3(p.eye, null);
  const target = num3(p.target, null);
  if (!eye || !target) return null;
  return { eye, target };
}

export function normalizeRoom(r, i = 0) {
  if (!r || typeof r !== "object") return null;
  const url = String(r.asset_url || r.url || r.file_url || "").trim();
  if (!url) return null;
  const format = String(r.format || "").toLowerCase() || formatFromUrl(url);
  const floor = Number.parseInt(r.floor_index ?? r.floor ?? 0, 10);
  return {
    id: String(r.id || `room-${i}`),
    name: String(r.name || "").trim() || (i === 0 ? "Whole home" : `Room ${i + 1}`),
    url,
    format: format || null,
    floor_index: Number.isFinite(floor) ? floor : 0,
    position: Number.isFinite(Number(r.position)) ? Number(r.position) : i,
    transform: normalizeTransform(r.transform),
    bbox: normalizeBox(r.bbox),
    entry_pose: normalizePose(r.entry_pose),
    thumbnail_url: r.thumbnail_url || null,
    hotspot: r.hotspot && Number.isFinite(Number(r.hotspot.x)) && Number.isFinite(Number(r.hotspot.y))
      ? { x: Number(r.hotspot.x), y: Number(r.hotspot.y) }
      : null,
  };
}

/** Normalize an API manifest, or wrap a legacy single file as one room. */
export function normalizeManifest(input) {
  if (!input || typeof input !== "object") return null;
  let rooms = Array.isArray(input.rooms)
    ? input.rooms.map((r, i) => normalizeRoom(r, i)).filter(Boolean)
    : [];
  if (!rooms.length) {
    const url = input.file_url || input.asset_url || input.url;
    const one = normalizeRoom({ id: input.id ? `${input.id}-main` : "main", name: "Whole home", url, format: input.format }, 0);
    if (one) rooms = [one];
  }
  if (!rooms.length) return null;
  rooms.sort((a, b) => a.floor_index - b.floor_index || a.position - b.position);
  const base = input.base && (input.base.asset_url || input.base.url)
    ? normalizeRoom({ ...input.base, id: "base", name: input.base.name || "Whole home" }, 0)
    : null;
  const floors = [...new Set(rooms.map((r) => r.floor_index))].sort((a, b) => a - b);
  const fp = input.floor_plan && typeof input.floor_plan === "object" ? input.floor_plan : null;
  return {
    id: input.id || null,
    title: input.title || "Virtual Visit",
    address: input.address || "",
    unbranded: Boolean(input.unbranded),
    branding: input.unbranded ? null : (input.branding || null),
    base,
    rooms,
    floors,
    floor_plans: fp && Array.isArray(fp.floors) ? fp.floors : (fp && fp.image_url ? [{ floor_index: 0, image_url: fp.image_url }] : []),
    start_room_id: rooms.some((r) => r.id === input.start_room_id) ? input.start_room_id : rooms[0].id,
  };
}

/** Visit is renderable by the splat viewer (any supported format). */
export function manifestIsRenderable(m) {
  return Boolean(m && m.rooms && m.rooms.length);
}

export function detectWebGL2(doc = typeof document !== "undefined" ? document : null) {
  try {
    if (!doc) return false;
    const canvas = doc.createElement("canvas");
    const gl = canvas.getContext && canvas.getContext("webgl2");
    return Boolean(gl);
  } catch {
    return false;
  }
}

export function visitPublicUrl(origin, visitId, { unbranded = false } = {}) {
  const base = String(origin || "https://app.brxs.ca").replace(/\/$/, "");
  const id = encodeURIComponent(String(visitId || ""));
  return unbranded ? `${base}/v/${id}?unbranded=1` : `${base}/v/${id}`;
}

/** Responsive iframe embed snippet for the desk. */
export function visitEmbedCode(origin, visitId, { unbranded = false, title = "Virtual Visit" } = {}) {
  const src = visitPublicUrl(origin, visitId, { unbranded }) + (unbranded ? "&embed=1" : "?embed=1");
  const safeTitle = String(title || "Virtual Visit").replace(/["<>&]/g, "");
  return (
    `<div style="position:relative;width:100%;padding-bottom:56.25%;height:0;overflow:hidden">` +
    `<iframe src="${src}" title="${safeTitle}" allow="fullscreen; xr-spatial-tracking; accelerometer; gyroscope" allowfullscreen loading="lazy" ` +
    `style="position:absolute;top:0;left:0;width:100%;height:100%;border:0"></iframe></div>`
  );
}

/** Rooms on one floor, in order. */
export function roomsOnFloor(manifest, floorIndex) {
  if (!manifest) return [];
  return manifest.rooms.filter((r) => r.floor_index === floorIndex);
}

export function floorLabel(i) {
  if (i < 0) return i === -1 ? "Basement" : `Basement ${-i}`;
  if (i === 0) return "Main floor";
  return i === 1 ? "Second floor" : `Floor ${i + 1}`;
}

/** Camera inside an axis-aligned box (manifest coordinates after transform). */
export function pointInBox(p, box, pad = 0) {
  if (!p || !box) return false;
  for (let k = 0; k < 3; k++) {
    if (p[k] < box.min[k] - pad || p[k] > box.max[k] + pad) return false;
  }
  return true;
}

/** Robust bounds from sampled centres: per-axis percentiles drop floaters. */
export function robustBounds(points, lo = 0.05, hi = 0.95) {
  if (!points || !points.length) return null;
  const axes = [[], [], []];
  for (const p of points) {
    if (p.every(Number.isFinite)) for (let k = 0; k < 3; k++) axes[k].push(p[k]);
  }
  if (!axes[0].length) return null;
  const min = [], max = [];
  for (let k = 0; k < 3; k++) {
    const a = axes[k].sort((x, y) => x - y);
    min.push(a[Math.floor(lo * (a.length - 1))]);
    max.push(a[Math.ceil(hi * (a.length - 1))]);
  }
  return { min, max };
}
